import './FilterOptionItemButton.scss';

import type { Dict } from '@types';
import type { ModifiersType } from '@utils';
import { appendClassNames, createClsNameMap, mapModifiers } from '@utils';
import { createElement } from 'react';

import FilterOptionItemAmount from '../Amount';

export const clsNameMap = createClsNameMap({
  modifiers: [
    'as-link',
    'as-button',
    'with-checkbox',
    'box',
    'items-center',
    'selected',
    'swatchSquare',
  ],
})('filter-option-item-button');

type ButtonBaseProps = {
  as: 'button' | 'link';
  label: React.ReactNode;
  adaLabel?: string;
  isSelected: boolean;
  docCount?: string | number;
  hasCheckbox?: boolean;
  btnLabelledBy?: string;
  title?: string;
  modifiers?: ModifiersType<typeof clsNameMap>;
  showFilterOptionCount?: boolean;
};

type ButtonLinkProps = ButtonBaseProps & {
  link: string;
  onClick?: (e: Event) => void;
};

type ButtonProps = ButtonBaseProps & {
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

export type FilterOptionItemButtonProps =
  | ({
      as: 'button';
    } & ButtonProps)
  | ({
      as: 'link';
    } & ButtonLinkProps);

const FilterOptionItemButton = (props: FilterOptionItemButtonProps) => {
  const {
    as,
    label,
    title,
    isSelected,
    docCount,
    hasCheckbox,
    btnLabelledBy,
    showFilterOptionCount,
  } = props;
  const elementType = as === 'link' ? 'a' : 'button';

  const propsByType = (): Readonly<Record<string, unknown>> => {
    switch (as) {
      case 'link': {
        const linkAttributes: Dict = {
          title: 'Link to',
          tabIndex: 0,
          'data-action': 'select-filter-item',
          href: props.link,
          'aria-current': isSelected ? true : undefined,
          onClick: props.onClick,
        };

        return linkAttributes;
      }

      case 'button': {
        const buttonAttributes: Dict = {
          title: title,
          role: 'checkbox',
          'aria-checked': isSelected,
          onClick: props.onClick,
        };

        // WCAG: only bind label when it have value
        if (btnLabelledBy) {
          buttonAttributes['aria-label'] = btnLabelledBy;
        }

        return buttonAttributes;
      }
      default:
        return {};
    }
  };

  return createElement(elementType, {
    ...propsByType(),
    className: appendClassNames(
      mapModifiers(clsNameMap, {
        'as-link': as === 'link',
        'as-button': as === 'button',
        'with-checkbox': hasCheckbox,
        selected: isSelected,
      })
    ),
    children: (
      <>
        {label}
        {showFilterOptionCount && docCount !== undefined && (
          <FilterOptionItemAmount isSelected={isSelected}>
            {`(` + docCount + `)`}
          </FilterOptionItemAmount>
        )}
      </>
    ),
  });
};

export default FilterOptionItemButton;
